import { css, cx } from "@emotion/css";
import { ExternalLink, Text, Button, Drawer, Tooltip } from "@octopusdeploy/design-system-components";
import { FileIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource } from "@octopusdeploy/octopus-server-client";
import { isRefApplicationSource, isHelmApplicationSource } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useMemo, useState } from "react";
import { CodeWithChrome } from "~/components/Code/CodeWithChrome";
import Select from "~/primitiveComponents/form/Select/Select";
import { ImageTemplateExpressionHelp } from "./ImageTemplateExpressionHelp";
import { createHelmImageAnnotations } from "./createHelmImageAnnotations";
const styles = {
    noMargin: css({
        margin: 0,
    }),
    mainContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[24],
        padding: space[16],
        paddingBottom: space[24],
    }),
    header: css({
        font: text.body.bold.large,
        marginTop: 0,
        marginBottom: space[16],
    }),
    contentContainer: css({
        paddingLeft: space[24],
        paddingRight: space[24],
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    selectContainer: css({
        width: "50%",
    }),
    expressionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        padding: space[16],
        boxShadow: themeTokens.shadow["extraSmall"],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius["large"],
    }),
    expressionHeaderContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[4],
        alignItems: "center",
        font: text.body.bold.medium,
    }),
    expressionInputContainer: css({
        width: "600px",
    }),
    note: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        margin: 0,
    }),
};
type RefSourceRow = {
    identifier: string;
    ref: string;
    repoUrl: string;
    valuesFilePath: string;
};
type GenerateArgoCDHelmAnnotationsDrawerInnerProps = {
    applications: ArgoCDApplicationResource[];
    initiallySelectedApplicationId: string | undefined;
};
const GenerateArgoCDHelmAnnotationsDrawerInner = (props: GenerateArgoCDHelmAnnotationsDrawerInnerProps) => {
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(props.initiallySelectedApplicationId);
    const [templateExpressions, setTemplateExpressions] = useState<Record<string, string | undefined>>({});
    //pre-populate the template expressions for the current application
    useEffect(() => {
        const app = props.applications.find((app) => app.Id === selectedApplicationId);
        setTemplateExpressions(app?.Sources.filter((src) => isRefApplicationSource(src)).reduce<Record<string, string | undefined>>((val, src) => {
            val[src.Ref] = undefined;
            return val;
        }, {}) || {});
    }, [props.applications, selectedApplicationId]);
    const items = useMemo(() => props.applications
        .filter((app) => app.Sources.some((src) => isHelmApplicationSource(src)))
        .map((app) => ({
        value: app.Id,
        text: app.Name,
    })), [props.applications]);
    const refSources: RefSourceRow[] = useMemo(() => {
        const app = props.applications.find((app) => app.Id === selectedApplicationId);
        if (!app) {
            return [];
        }
        return app.Sources.filter((src) => isRefApplicationSource(src)).flatMap((refSrc) => {
            const refPath = `$${refSrc.Ref}/`;
            //get a distinct list of the values files from all helm application sources
            const valuesFiles = [
                ...new Set(app.Sources.filter((src) => isHelmApplicationSource(src))
                    .flatMap((helmSrc) => helmSrc.ValueFiles)
                    .filter((valuesFile) => valuesFile.startsWith(refPath))),
            ];
            return valuesFiles.map((path) => ({
                identifier: path,
                ref: refSrc.Ref,
                repoUrl: refSrc.RepositoryUrl.replace(/\.git$/, ""),
                valuesFilePath: path.replace(refPath, ""),
            }));
        });
    }, [props.applications, selectedApplicationId]);
    const annotations = useMemo(() => createHelmImageAnnotations(templateExpressions), [templateExpressions]);
    return (<div className={styles.mainContainer}>
            <div>
                <h2 className={styles.header}>1. Select an Argo CD application</h2>
                <div className={styles.contentContainer}>
                    <div className={styles.selectContainer}>
                        <Select items={items} value={selectedApplicationId} onChange={setSelectedApplicationId} label={"Application"} allowClear={false} allowFilter={false}/>
                    </div>
                </div>
            </div>
            <div>
                <h2 className={styles.header}>
                    <span>2. Add the image template expression(s)</span> <ImageTemplateExpressionHelp />
                </h2>
                <div className={styles.contentContainer}>
                    <p className={styles.noMargin}>
                        Provide the expression that defines the container image in your Helm chart, e.g. <code>{"{{ .Values.image.repository }}:{{ .Values.image.tag }}"}</code>
                    </p>
                    {refSources.map((src) => (<div key={src.identifier} className={styles.expressionContainer}>
                            <p className={cx(styles.expressionHeaderContainer, styles.noMargin)}>
                                <Tooltip content={"Helm values file"}>
                                    <FileIcon size={24}/>
                                </Tooltip>
                                <span>
                                    {src.repoUrl}/{src.valuesFilePath}
                                </span>
                            </p>
                            <div className={styles.expressionInputContainer}>
                                <Text label={"Image template expression"} value={templateExpressions[src.ref]} onChange={(val) => setTemplateExpressions((prev) => ({
                ...prev,
                [src.ref]: val,
            }))}/>
                            </div>
                        </div>))}
                </div>
            </div>
            <div>
                <h2 className={styles.header}>3. Copy the annotations to your Argo CD application</h2>
                <div className={styles.contentContainer}>
                    <CodeWithChrome value={annotations} language={"YAML"} lineWrapping={true} showLineNumbers={false} showCodeFold={false} canCopy={true}/>
                    <p className={styles.note}>
                        Need help with annotations? <ExternalLink label={"Read the docs"} href={"argo-cd-helm-image-annotations"} size={"small"}/>
                    </p>
                </div>
            </div>
        </div>);
};
type GenerateArgoCDHelmAnnotationsDrawer = GenerateArgoCDHelmAnnotationsDrawerInnerProps & {
    open: boolean;
    onClose: () => void;
};
export const GenerateArgoCDHelmAnnotationsDrawer = (props: GenerateArgoCDHelmAnnotationsDrawer) => {
    const { open, onClose, ...rest } = props;
    return (<Drawer variant={"customActions"} isOpen={open} onClose={onClose} title={"Generate image annotations for Helm"} width={"wide"} layout={"basic"} customActions={[<Button importance={"secondary"} onClick={onClose} label="Close"/>]}>
            <GenerateArgoCDHelmAnnotationsDrawerInner {...rest}/>
        </Drawer>);
};
